import {makeAutoObservable, runInAction} from 'mobx';
import * as api from '../service/DeclutterApi';
import {
    DeclutterAction,
    DeclutterItemCard,
    DeclutterOverview,
    DeclutterPhysicalStep,
    DeclutterQueueItem,
    DeclutterReport,
    DeclutterSession,
    SaleListing,
    SaleMarketplace,
    apiErrorCode,
} from '../service/DeclutterApi';
import {startErrorMessage} from '../support/declutterLabels';
import logger from '../utils/logger';

/**
 * Стадия страницы разбора. Выводится из статуса последней сессии и хвостов после неё, а не из
 * «есть ли открытая сессия»: после «Завершить решения» открытой сессии нет, но отчёт, физический
 * шаг и объявления — именно про неё.
 */
export type DeclutterStage = 'overview' | 'decisions' | 'aftercare';

/** Статусы, в которых сессия принимает решения. */
const OPEN_STATUSES = new Set(['DRAFT', 'IN_PROGRESS']);

/** Ошибка старта: код backend (или null для сетевых сбоев) и готовая подпись. */
export interface StartError {
    code: string | null;
    message: string;
}

/**
 * MobX-стор страницы «Разбор гардероба» (по образцу StylistStore).
 *
 * Одна точка загрузки обзора, сессии, очереди, карточки, физического шага и объявлений; тихий
 * refresh без спиннера всей страницы; точечное обновление очереди по результату решения вместо
 * перезапроса; отмена устаревших ответов при смене владельца (токен запроса — тот же приём, что
 * флаг `cancelled` в DeclutterObjections); генерация дуэлей — один раз на сессию.
 */
class DeclutterStore {
    overview: DeclutterOverview | null = null;
    session: DeclutterSession | null = null;
    queue: DeclutterQueueItem[] = [];
    card: DeclutterItemCard | null = null;
    report: DeclutterReport | null = null;
    pending: DeclutterPhysicalStep | null = null;
    listings: SaleListing[] = [];
    marketplaces: SaleMarketplace[] = [];
    history: DeclutterSession[] = [];

    /** Первая загрузка — спиннер страницы; refresh идёт тихо. */
    loading = false;
    refreshing = false;
    deciding = false;
    starting = false;
    completing = false;
    error: string | null = null;
    startError: StartError | null = null;
    /** Фильтр очереди по категории (FR-10); null — вся очередь. */
    categoryId: string | null = null;
    /** Пользователь из стадии «после решений» захотел новый разбор — показываем стартовую панель. */
    startPanelForced = false;

    /** Результат последнего пересчёта оценки: время и дифф «было → стало» (аудит 17.09, D03). */
    reassessment: api.DeclutterReassessment | null = null;
    /** Пересчёт идёт. */
    reassessing = false;
    /** Пересчёт не удался — ошибку видно, а не только в консоли (D03). */
    reassessError: string | null = null;

    private ownerId: string | null = null;
    /** Токен актуального запроса: ответы с другим токеном (старый владелец) отбрасываются. */
    private loadToken = 0;
    private duelsGeneratedFor = new Set<string>();

    constructor() {
        makeAutoObservable(this);
    }

    get stage(): DeclutterStage {
        if (this.session && OPEN_STATUSES.has(this.session.status)) {
            return 'decisions';
        }
        if (this.startPanelForced) {
            return 'overview';
        }
        const decisionsDone = this.session?.status === 'DECISIONS_DONE';
        const hasTail = (this.pending?.items.length ?? 0) > 0 || this.listings.length > 0;
        return decisionsDone || hasTail ? 'aftercare' : 'overview';
    }

    /** Очередь в текущем фильтре категории. */
    get pendingCount(): number {
        return this.queue.filter(q => q.status === 'PENDING').length;
    }

    /** Полная загрузка страницы; при смене владельца прежние ответы отбрасываются. */
    load = async (ownerId: string | null): Promise<void> => {
        this.ownerId = ownerId;
        this.loading = true;
        this.error = null;
        this.startError = null;
        this.startPanelForced = false;
        this.categoryId = null;
        await this.fetchAll(ownerId, ++this.loadToken);
        runInAction(() => {
            this.loading = false;
        });
    };

    /** Тихое обновление: данные меняются на месте, спиннер страницы не показывается. */
    refresh = async (): Promise<void> => {
        this.refreshing = true;
        await this.fetchAll(this.ownerId, ++this.loadToken);
        runInAction(() => {
            this.refreshing = false;
        });
    };

    private isCurrent(token: number): boolean {
        return token === this.loadToken;
    }

    private async fetchAll(ownerId: string | null, token: number): Promise<void> {
        try {
            const data = await api.fetchOverview(ownerId);
            if (!this.isCurrent(token)) return;
            const last = data.openSession ?? data.lastSession ?? null;
            const [tail, sessionData] = await Promise.all([
                this.fetchTail(ownerId),
                last ? this.fetchSessionData(ownerId, last, this.categoryId) : null,
            ]);
            if (!this.isCurrent(token)) return;
            runInAction(() => {
                this.overview = data;
                this.session = last;
                this.pending = tail.pending;
                this.listings = tail.listings;
                this.marketplaces = tail.marketplaces;
                this.history = tail.history;
                if (sessionData) {
                    this.queue = sessionData.queue;
                    this.card = sessionData.card;
                    this.report = sessionData.report;
                } else {
                    this.queue = [];
                    this.card = null;
                    this.report = null;
                }
            });
        } catch (e) {
            logger.debug('DeclutterStore: load failed', {error: (e as Error)?.message});
            if (this.isCurrent(token)) {
                runInAction(() => {
                    this.error = 'Не удалось загрузить разбор гардероба';
                });
            }
        }
    }

    /**
     * Хвосты после решений и история. Их отсутствие не должно ронять страницу: старый бэкенд
     * без истории или без объявлений — всё равно рабочий обзор.
     */
    private async fetchTail(ownerId: string | null) {
        const [pending, listings, marketplaces, history] = await Promise.all([
            api.fetchPending(ownerId).catch(() => null),
            api.fetchListings(ownerId, true).catch(() => [] as SaleListing[]),
            api.fetchMarketplaces(ownerId).catch(() => [] as SaleMarketplace[]),
            api.fetchHistory(ownerId).then(p => p.content).catch(() => [] as DeclutterSession[]),
        ]);
        return {pending, listings, marketplaces, history};
    }

    private async fetchSessionData(ownerId: string | null, session: DeclutterSession,
                                   categoryId: string | null) {
        const open = OPEN_STATUSES.has(session.status);
        const [queue, report] = await Promise.all([
            open ? api.fetchQueue(ownerId, session.id, categoryId ?? undefined) : [],
            api.fetchReport(ownerId, session.id).catch(() => null),
        ]);
        const next = queue.find(i => i.status === 'PENDING') ?? queue[0];
        const card = next ? await api.fetchCard(ownerId, session.id, next.itemId) : null;
        return {queue, card, report};
    }

    /** Старт разбора. Ошибки различаются по коду backend, а не сливаются в одну фразу. */
    start = async (mode: string, quick: boolean, newGraceDays?: number): Promise<void> => {
        const ownerId = this.ownerId;
        const token = ++this.loadToken;
        this.starting = true;
        this.startError = null;
        this.error = null;
        try {
            const started = await api.startSession(ownerId, mode, quick, undefined, newGraceDays);
            this.duelsGeneratedFor.delete(started.id);
            const sessionData = await this.fetchSessionData(ownerId, started, null);
            if (!this.isCurrent(token)) return;
            runInAction(() => {
                this.session = started;
                this.startPanelForced = false;
                this.categoryId = null;
                this.queue = sessionData.queue;
                this.card = sessionData.card;
                this.report = sessionData.report;
            });
        } catch (e) {
            const code = apiErrorCode(e);
            logger.debug('DeclutterStore: start failed', {code});
            if (this.isCurrent(token)) {
                runInAction(() => {
                    this.startError = {
                        code,
                        message: startErrorMessage(code),
                    };
                });
            }
        } finally {
            if (this.isCurrent(token)) {
                runInAction(() => {
                    this.starting = false;
                });
            }
        }
    };

    clearStartError = (): void => {
        this.startError = null;
    };

    /** Из стадии «после решений» — к стартовой панели нового разбора. */
    openStartPanel = (): void => {
        this.startPanelForced = true;
    };

    /**
     * Одно решение на всю категорию текущей вещи (правка владельца 17.09). Очередь категории
     * берётся с сервера, решения пишутся по одной вещи — пакетного эндпоинта нет, а откат
     * по вещи и аудит должны совпадать с одиночным решением. Устаревшая оценка любой вещи
     * останавливает проход: правило D03 для пакета не ослабляется.
     *
     * @return сколько вещей получили решение
     */
    decideCategory = async (action: DeclutterAction, categoryId: string): Promise<number> => {
        const {session, ownerId} = this;
        if (!session) return 0;
        const token = this.loadToken;
        this.deciding = true;
        this.error = null;
        let done = 0;
        try {
            const items = await api.fetchQueue(ownerId, session.id, categoryId);
            const targets = items.filter(q => !q.decidedAction);
            let last: api.DecisionResult | null = null;
            for (const target of targets) {
                last = await api.recordDecision(ownerId, session.id, target.itemId, action, false);
                done++;
                if (!this.isCurrent(token)) return done;
                const progress = last.progress;
                runInAction(() => {
                    this.session = {...session, progress};
                    this.queue = this.queue.map(q => q.itemId === target.itemId
                        ? {...q, decidedAction: action, status: 'DECIDED'}
                        : q);
                });
            }
            if (last) {
                const next = last.nextItemId
                    ? await api.fetchCard(ownerId, session.id, last.nextItemId)
                    : null;
                if (!this.isCurrent(token)) return done;
                runInAction(() => {
                    this.card = next;
                    this.reassessment = null;
                    this.reassessError = null;
                });
                void this.refreshPending();
            }
        } catch (e) {
            logger.debug('DeclutterStore: category decision failed', {error: (e as Error)?.message, done});
            if (this.isCurrent(token)) {
                runInAction(() => {
                    this.error = apiErrorCode(e) === 'ASSESSMENT_OUTDATED'
                        ? `Остановлено после ${done}: у следующей вещи оценка устарела — обновите её в карточке`
                        : `Не удалось применить решение ко всей категории (сохранено: ${done})`;
                });
            }
        } finally {
            if (this.isCurrent(token)) {
                runInAction(() => {
                    this.deciding = false;
                });
            }
        }
        return done;
    };

    /**
     * Решение по текущей карточке. Очередь правится на месте по `DecisionResult`: перезапрос
     * всей очереди на каждое нажатие — лишний round-trip и мигание списка.
     */
    decide = async (action: DeclutterAction, acceptOutdated = false): Promise<void> => {
        const {session, card, ownerId} = this;
        if (!session || !card) return;
        const token = this.loadToken;
        this.deciding = true;
        this.error = null;
        try {
            const result = await api.recordDecision(ownerId, session.id, card.itemId, action,
                acceptOutdated);
            const next = result.nextItemId
                ? await api.fetchCard(ownerId, session.id, result.nextItemId)
                : null;
            if (!this.isCurrent(token)) return;
            runInAction(() => {
                this.session = {...session, progress: result.progress};
                this.queue = this.queue.map(q => q.itemId === card.itemId
                    ? {...q, decidedAction: action, status: 'DECIDED'}
                    : q);
                this.card = next;
                // Дифф относится к вещи, с которой мы уже ушли.
                this.reassessment = null;
                this.reassessError = null;
            });
            // Решение сразу кладёт вещь в корзину — физический шаг на той же странице
            // должен это увидеть, не дожидаясь «Завершить решения» (Q5).
            void this.refreshPending();
        } catch (e) {
            logger.debug('DeclutterStore: decision failed', {error: (e as Error)?.message});
            if (this.isCurrent(token)) {
                runInAction(() => {
                    // Устаревшая оценка — не сбой, а осознанная блокировка (D03): пользователю
                    // предлагают обновить расчёт либо подтвердить решение по текущим данным.
                    this.error = apiErrorCode(e) === 'ASSESSMENT_OUTDATED'
                        ? 'Вещь изменилась после расчёта оценки — обновите оценку или решите по текущим данным'
                        : 'Не удалось сохранить решение';
                });
            }
        } finally {
            if (this.isCurrent(token)) {
                runInAction(() => {
                    this.deciding = false;
                });
            }
        }
    };

    /**
     * Отмена последнего решения (Q13). Возвращает вещь в очередь, откатывает прогресс и
     * открывает её карточку: «Отменить» в тосте должно возвращать ровно туда, откуда ушли.
     */
    undoDecision = async (itemId: string): Promise<boolean> => {
        const {session, ownerId} = this;
        if (!session) return false;
        const token = this.loadToken;
        try {
            const result = await api.undoDecision(ownerId, session.id, itemId);
            const restored = await api.fetchCard(ownerId, session.id, itemId).catch(() => null);
            if (!this.isCurrent(token)) return false;
            runInAction(() => {
                this.session = {...session, progress: result.progress};
                this.queue = this.queue.map(q => q.itemId === itemId
                    ? {...q, decidedAction: null, status: 'PENDING'}
                    : q);
                if (restored) this.card = restored;
            });
            void this.refreshPending();
            return true;
        } catch (e) {
            logger.debug('DeclutterStore: undo failed', {error: (e as Error)?.message});
            if (this.isCurrent(token)) {
                runInAction(() => {
                    this.error = 'Не удалось отменить решение';
                });
            }
            return false;
        }
    };

    /**
     * Тихое обновление только физического шага: решение и его отмена меняют содержимое
     * корзины, а перезагружать ради этого всю страницу незачем.
     */
    refreshPending = async (): Promise<void> => {
        const token = this.loadToken;
        const pending = await api.fetchPending(this.ownerId).catch(() => null);
        if (!this.isCurrent(token) || pending == null) return;
        runInAction(() => {
            this.pending = pending;
        });
    };

    /**
     * Пересчёт оценки вещи (аудит 17.09, D03). Прежняя кнопка «Обновить оценку» просто
     * перезапрашивала карточку и ничего не меняла: предупреждение оставалось, время расчёта
     * не появлялось. Теперь ответ несёт время и дифф, а сбой виден пользователю.
     */
    reassess = async (): Promise<void> => {
        const {session, card, ownerId} = this;
        if (!session || !card) return;
        const token = this.loadToken;
        this.reassessing = true;
        this.reassessError = null;
        try {
            const result = await api.reassessItem(ownerId, session.id, card.itemId);
            if (!this.isCurrent(token)) return;
            runInAction(() => {
                this.reassessment = result;
                this.card = result.card;
                this.queue = this.queue.map(q => q.itemId === result.card.itemId
                    ? {...q, score: result.card.score,
                        recommendedAction: result.card.recommendedAction}
                    : q);
            });
        } catch (e) {
            logger.debug('DeclutterStore: reassess failed', {error: (e as Error)?.message});
            if (this.isCurrent(token)) {
                runInAction(() => {
                    this.reassessError = 'Не удалось пересчитать оценку. Попробуйте ещё раз.';
                });
            }
        } finally {
            if (this.isCurrent(token)) {
                runInAction(() => {
                    this.reassessing = false;
                });
            }
        }
    };

    /**
     * «Сначала попробовать» (аудит 17.09, D10): вещь уходит на восстановление или проверку,
     * позиция очереди откладывается до даты возврата.
     */
    tryFirst = async (kind: api.RestorationKind, dueDate?: string | null,
                      note?: string | null): Promise<boolean> => {
        const {session, card, ownerId} = this;
        if (!session || !card) return false;
        const token = this.loadToken;
        const itemId = card.itemId;
        this.deciding = true;
        this.error = null;
        try {
            const result = await api.tryFirst(ownerId, session.id, itemId, kind, dueDate, note);
            const next = result.nextItemId
                ? await api.fetchCard(ownerId, session.id, result.nextItemId)
                : null;
            if (!this.isCurrent(token)) return false;
            runInAction(() => {
                this.session = {...session, progress: result.progress};
                this.queue = this.queue.map(q => q.itemId === itemId
                    ? {...q, status: 'DEFERRED', decidedAction: null}
                    : q);
                this.card = next;
                this.reassessment = null;
            });
            void this.refreshPending();
            return true;
        } catch (e) {
            logger.debug('DeclutterStore: tryFirst failed', {error: (e as Error)?.message});
            if (this.isCurrent(token)) {
                runInAction(() => {
                    this.error = 'Не удалось отправить вещь на восстановление';
                });
            }
            return false;
        } finally {
            if (this.isCurrent(token)) {
                runInAction(() => {
                    this.deciding = false;
                });
            }
        }
    };

    /** Открыть конкретную позицию очереди (например, вернуться к уже решённой). */
    openItem = async (itemId: string): Promise<void> => {
        const {session, ownerId} = this;
        if (!session) return;
        const token = this.loadToken;
        try {
            const next = await api.fetchCard(ownerId, session.id, itemId);
            if (!this.isCurrent(token)) return;
            runInAction(() => {
                this.card = next;
                this.reassessment = null;
                this.reassessError = null;
            });
        } catch (e) {
            logger.debug('DeclutterStore: card failed', {error: (e as Error)?.message});
        }
    };

    /** Фильтр очереди по категории (FR-10): очередь перезапрашивается с `categoryId`. */
    setCategory = async (categoryId: string | null): Promise<void> => {
        const {session, ownerId} = this;
        this.categoryId = categoryId;
        if (!session || !OPEN_STATUSES.has(session.status)) return;
        const token = this.loadToken;
        try {
            const data = await this.fetchSessionData(ownerId, session, categoryId);
            if (!this.isCurrent(token) || this.categoryId !== categoryId) return;
            runInAction(() => {
                this.queue = data.queue;
                this.card = data.card;
            });
        } catch (e) {
            logger.debug('DeclutterStore: category filter failed', {error: (e as Error)?.message});
            if (this.isCurrent(token)) {
                runInAction(() => {
                    this.error = 'Не удалось отфильтровать очередь';
                });
            }
        }
    };

    /**
     * «Завершить решения»: сессия переходит в DECISIONS_DONE и остаётся на странице как
     * последняя — стадия «после решений» показывает отчёт, физический шаг и объявления.
     */
    complete = async (): Promise<void> => {
        const {session, ownerId} = this;
        if (!session) return;
        const token = ++this.loadToken;
        this.completing = true;
        this.error = null;
        try {
            const done = await api.completeSession(ownerId, session.id);
            const [report, tail] = await Promise.all([
                api.fetchReport(ownerId, session.id).catch(() => null),
                this.fetchTail(ownerId),
            ]);
            if (!this.isCurrent(token)) return;
            runInAction(() => {
                this.session = done;
                this.report = report;
                this.queue = [];
                this.card = null;
                this.pending = tail.pending;
                this.listings = tail.listings;
                this.marketplaces = tail.marketplaces;
                this.history = tail.history;
            });
        } catch (e) {
            logger.debug('DeclutterStore: complete failed', {error: (e as Error)?.message});
            if (this.isCurrent(token)) {
                runInAction(() => {
                    this.error = 'Не удалось завершить разбор';
                });
            }
        } finally {
            if (this.isCurrent(token)) {
                runInAction(() => {
                    this.completing = false;
                });
            }
        }
    };

    /** Генерация дуэлей — один раз на сессию; повторные вызовы карточки дуэлей не ходят на сервер. */
    generateDuelsOnce = async (sessionId: string): Promise<void> => {
        if (this.duelsGeneratedFor.has(sessionId)) return;
        this.duelsGeneratedFor.add(sessionId);
        try {
            await api.generateDuels(this.ownerId, sessionId);
        } catch (e) {
            this.duelsGeneratedFor.delete(sessionId);
            throw e;
        }
    };

    /** Сброс между тестами и при выходе из аккаунта. */
    reset = (): void => {
        this.loadToken += 1;
        this.overview = null;
        this.session = null;
        this.queue = [];
        this.card = null;
        this.report = null;
        this.pending = null;
        this.listings = [];
        this.marketplaces = [];
        this.history = [];
        this.loading = false;
        this.refreshing = false;
        this.deciding = false;
        this.starting = false;
        this.completing = false;
        this.error = null;
        this.startError = null;
        this.categoryId = null;
        this.startPanelForced = false;
        this.ownerId = null;
        this.duelsGeneratedFor = new Set();
    };
}

const declutterStore = new DeclutterStore();
export default declutterStore;
export {DeclutterStore};
